// =============================================================================
// features/meu-whatsapp/useWhatsAppConnection.ts — Estado da conexão do
// WhatsApp do usuário logado (F57-S02), de `GET /api/me/whatsapp-connection`.
//
// Chave `['meu-whatsapp','connection']`: o evento de socket
// `channel.session_status` (useSessionStatusRealtime) já invalida o prefixo
// `['meu-whatsapp']`, então uma queda ou reconexão chega aqui sem polling novo.
// O `refetchInterval` de 90 s e a revalidação ao focar a aba são a rede de
// segurança para quando o socket não entrega.
//
// Falha da consulta (403 flag/primeiro acesso, 429, rede) = `data` indefinido
// = nada aparece. Fail-closed: na dúvida, não alarmar.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { create } from 'zustand';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { api } from '../../lib/api';

import {
  CONNECTION_GUARD_FLAG,
  CONNECTION_REASONS,
  CONNECTION_STATUSES,
  type WhatsAppConnection,
} from './connectionGuard';
import { MEU_WHATSAPP_KEY } from './useMeuWhatsApp';

const WhatsAppConnectionSchema = z.object({
  required: z.boolean(),
  agent_id: z.string().uuid().nullable(),
  channel_id: z.string().uuid().nullable(),
  status: z.enum(CONNECTION_STATUSES),
  reason: z.enum(CONNECTION_REASONS).nullable(),
  since: z.string().nullable(),
  checked_at: z.string().nullable(),
  needs_action: z.boolean(),
});

export const WHATSAPP_CONNECTION_KEY = [...MEU_WHATSAPP_KEY, 'connection'] as const;

/** Flag da guarda ligada para este usuário (camada UI). */
export function useConnectionGuardEnabled(): boolean {
  return useFeatureFlag(CONNECTION_GUARD_FLAG).enabled;
}

export function useWhatsAppConnection(
  enabled: boolean,
): ReturnType<typeof useQuery<WhatsAppConnection>> {
  return useQuery<WhatsAppConnection>({
    queryKey: WHATSAPP_CONNECTION_KEY,
    queryFn: async () =>
      WhatsAppConnectionSchema.parse(await api.get('/api/me/whatsapp-connection')),
    enabled,
    staleTime: 30_000,
    refetchInterval: 90_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Modal aberto/fechado — compartilhado entre o selo (Topbar) e a guarda
// (AppLayout), que vivem em árvores diferentes.
// ---------------------------------------------------------------------------

interface ConnectionGuardStore {
  readonly modalOpen: boolean;
  readonly openModal: () => void;
  readonly closeModal: () => void;
}

export const useConnectionGuardStore = create<ConnectionGuardStore>()((set) => ({
  modalOpen: false,
  openModal: () => set({ modalOpen: true }),
  closeModal: () => set({ modalOpen: false }),
}));
